import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useForm } from 'react-hook-form';
import { JwtSignInSocials } from '../../JwtAuthProvider';
import useJwtAuth from '../../useJwtAuth';
import {
  AlternativeMethod,
  ApiError,
  defaultSignInFormValues,
  MfaType,
  signInFormSchema,
  SignInFormType,
} from './types';

/**
 * Return type for the useJwtSignInForm hook
 */
export interface UseJwtSignInFormReturn {
  // Form state
  control: ReturnType<typeof useForm<SignInFormType>>['control'];
  formState: ReturnType<typeof useForm<SignInFormType>>['formState'];
  handleSubmit: ReturnType<typeof useForm<SignInFormType>>['handleSubmit'];
  setError: ReturnType<typeof useForm<SignInFormType>>['setError'];
  trigger: ReturnType<typeof useForm<SignInFormType>>['trigger'];

  // UI state
  passwordVisibility: boolean;
  loading: boolean;
  otpRequired: boolean;
  showSocialOtp: boolean;
  socialIdentifier: string | null;
  accessToken: string | null;

  // MFA state
  mfaType: MfaType | null;
  initialType: MfaType | null;
  alternativeMethods: AlternativeMethod[];

  // Handlers
  onSubmit: (formData: SignInFormType) => Promise<void>;
  togglePasswordVisibility: () => void;
  toggleMfaMode: () => void;
  handleSocialOtpSuccess: () => void;
  isSubmitDisabled: () => boolean;
  requestAlternativeOtp: (method: string) => Promise<void>;
  panelLogin?: (flag: string) => Promise<Response>;
  signOut?: (options?: { redirect?: boolean }) => void;
}

/**
 * Custom hook for JWT Sign-In Form logic
 * 
 * Encapsulates all the business logic, state management, and handlers
 * for the JWT authentication form, including:
 * - Form validation and submission
 * - Password visibility toggle
 * - MFA verification flow
 * - Social authentication handling
 * - Error handling and validation
 * 
 * @returns {UseJwtSignInFormReturn} Hook return object with form state and handlers
 */
export function useJwtSignInForm(): UseJwtSignInFormReturn {
  const { signIn, otpSignIn, signInWithSocials, panelLogin, signOut } = useJwtAuth();

  // Form state management
  const [passwordVisibility, setPasswordVisibility] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Authentication state
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [otpRequired, setOtpRequired] = useState<boolean>(false);
  const [showSocialOtp, setShowSocialOtp] = useState<boolean>(false);
  const [socialIdentifier, setSocialIdentifier] = useState<string | null>(null);

  // MFA state management
  const [mfaType, setMfaType] = useState<MfaType | null>(null);
  const [initialType, setInitialType] = useState<MfaType | null>(null);
  const [alternativeMethods, setAlternativeMethods] = useState<AlternativeMethod[]>([]);

  // Use ref to track current method reliably for autosubmit scenarios
  const currentMethodRef = useRef<MfaType | null>(null);

  const [cookies, , removeCookie] = useCookies(['mfa-remember']);

  const { control, formState, handleSubmit, setError, trigger } =
    useForm<SignInFormType>({
      mode: 'onChange',
      defaultValues: defaultSignInFormValues,
      resolver: zodResolver(signInFormSchema),
    });

  const { isValid, dirtyFields, touchedFields } = formState;

  // Sync ref with mfaType state
  useEffect(() => {
    currentMethodRef.current = mfaType;
  }, [mfaType]);

  /**
   * Handles authentication errors and sets appropriate form field errors
   */
  const handleAuthenticationError = useCallback((apiError: ApiError, otp: string | undefined) => {
    const errorDetail = apiError.data?.detail;

    if (otp) {
      // OTP validation errors
      setError('otp', {
        type: 'manual',
        message: errorDetail || 'Failed to verify OTP. Please try again.',
      });
    } else {
      // Login errors
      let message = errorDetail === 'No active account found with the given credentials'
        ? 'Unable to log in with provided credentials.'
        : 'Request could not be completed.';

      // Handle rate limiting errors
      if (errorDetail === 'Login attempts are too frequent.') {
        message = 'Login attempts are too frequent.';
      } else if (errorDetail === 'Too many login attempts. Please try again later.') {
        message = 'Too many login attempts. Please try again later.';
      } else if (errorDetail === 'Rate limit exceeded. Please try again later.') {
        message = 'Rate limit exceeded. Please try again later.';
      }

      setError('username', { type: 'manual', message });
      setError('password', { type: 'manual', message });
    }
  }, [setError]);

  /**
   * Triggers form validation on dirty fields change
   */
  useEffect(() => {
    if (Object.keys(dirtyFields).length && !Object.keys(touchedFields).length) {
      trigger();
    }
  }, [dirtyFields, touchedFields, trigger]);

  /**
   * Handles form submission for both initial sign-in and OTP verification
   */
  const onSubmit = useCallback(async (formData: SignInFormType): Promise<void> => {
    const { username, password, otp, remember_me_duration, recovery_code } = formData;
    setLoading(true);

    try {
      if (!otp && !recovery_code) {
        // Initial sign in attempt
        await signIn({
          username,
          password,
          remember_me_token: cookies['mfa-remember'],
        });
      } else {
        // OTP verification attempt for email/password login
        const payload: SignInFormType = {
          username,
          password,
          otp,
          remember_me_duration,
          recovery_code,
        };

        // Include method to ensure backend validates against the correct method
        const currentMethod = currentMethodRef.current || mfaType;

        if (currentMethod) {
          payload.method = currentMethod;
        }

        await otpSignIn(payload);
      }
    } catch (error: unknown) {


      if ((error as Error).message === 'no_roles') {
        setLoading(false);
        setError('username', { type: 'manual', message: 'No roles assigned. Please contact your account owner.' });
        setError('password', { type: 'manual', message: 'No roles assigned. Please contact your account owner.' });
        return;
      }

      const apiError = error as ApiError;

      // Handle invalid remember me token
      if (apiError.data?.error === 'Invalid Remember Me token') {
        removeCookie('mfa-remember');
        try {
          await signIn({ username, password });
        } catch (err) {
          setLoading(false);
          throw err;
        }
        return;
      }

      setLoading(false);

      // Handle 2FA requirement
      if (apiError.data?.error === '2FA_REQ') {
        setOtpRequired(true);
        const mfaTypeValue = apiError.data['2fa_type'] as MfaType;
        setMfaType(mfaTypeValue);
        setInitialType(mfaTypeValue);
        currentMethodRef.current = mfaTypeValue; // Update ref for initial method
        // Capture alternative methods from backend response
        setAlternativeMethods(apiError.data.alternative_methods || []);
        return;
      }

      // Handle validation errors
      handleAuthenticationError(apiError, otp);
    }
  }, [signIn, otpSignIn, cookies, removeCookie, handleAuthenticationError, setError, mfaType]);

  /**
   * Handles Google authentication with OAuth code
   */
  const handleGoogleAuth = useCallback((code: string): void => {
    if (accessToken !== code) {
      setAccessToken(code);
      setLoading(true);

      const request: JwtSignInSocials = {
        token: code,
        provider: 'google'
      };

      if (import.meta.env.DEV) {
        request.local = 'true';
      }

      signInWithSocials(request)
        .catch((error: unknown) => {
          const apiError = error as ApiError;

          if (apiError.data?.error === '2FA required') {
            setShowSocialOtp(true);
            const mfaTypeValue = apiError.data['2fa_type'] as MfaType;
            setMfaType(mfaTypeValue);
            setInitialType(mfaTypeValue);
            setSocialIdentifier(apiError.data.identifier || null);
            return;
          }

          setAccessToken('');
          const errorDetail = apiError.data?.detail;

          let errorMessage = 'Could not login with social account.';

          // Handle rate limiting errors for social login
          if (errorDetail === 'Login attempts are too frequent.' ||
            errorDetail === 'Too many login attempts. Please try again later.' ||
            errorDetail === 'Rate limit exceeded. Please try again later.') {
            errorMessage = errorDetail;
          }

          setError('username', { type: 'manual', message: errorMessage });
          setError('password', { type: 'manual', message: errorMessage });
        })
        .finally(() => {
          globalThis.window.history.replaceState({}, document.title, globalThis.window.location.pathname);
          setLoading(false);
        });
    }
  }, [signInWithSocials, accessToken, setError]);

  /**
   * Effect to handle Google OAuth authentication from URL parameters
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(globalThis.window.location.search);
    const code = urlParams.get('code');

    if (code && code !== accessToken) {
      handleGoogleAuth(code);
    }
  }, [handleGoogleAuth, accessToken]);

  /**
   * Handler for successful social OTP verification
   */
  const handleSocialOtpSuccess = useCallback((): void => {
    setShowSocialOtp(false);
    setSocialIdentifier(null);
  }, []);

  /**
   * Toggles password visibility in the password field
   */
  const togglePasswordVisibility = useCallback((): void => {
    setPasswordVisibility(!passwordVisibility);
  }, [passwordVisibility]);

  /**
   * Toggles between OTP/TOTP and recovery code authentication
   */
  const toggleMfaMode = useCallback((): void => {
    if (mfaType === 'recovery') {
      setMfaType(initialType);
    } else {
      setMfaType('recovery');
    }
  }, [mfaType, initialType]);

  /**
   * Determines if the submit button should be disabled based on form state
   */
  const isSubmitDisabled = useCallback((): boolean => {
    if (loading) return true;

    if (!otpRequired) {
      return isEmpty(dirtyFields) || !isValid;
    }

    if (otpRequired && !socialIdentifier) {
      if (mfaType === 'recovery') {
        return !formState.dirtyFields.recovery_code;
      }

      if (mfaType === 'totp' || mfaType === 'email') {
        return !formState.dirtyFields.otp;
      }
    }

    return false;
  }, [loading, otpRequired, socialIdentifier, mfaType, formState.dirtyFields, dirtyFields, isValid]);

  /**
   * Request OTP for an alternative 2FA method
   */
  const requestAlternativeOtp = useCallback(async (method: string): Promise<void> => {
    const { username, password } = control._getWatch();

    if (!username || !password) {
      setError('username', { type: 'manual', message: 'Username and password are required' });
      return;
    }

    setLoading(true);

    try {
      const { apiRoutes } = await import('@auth/authApi');
      const apiFetch = (await import('@/utils/apiFetch')).default;

      const response = await apiFetch(apiRoutes.requestOtp, {
        method: 'POST',
        body: JSON.stringify({
          username,
          password,
          method,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to request OTP');
      }

      // Switch to the new method and update alternative methods
      const previousMethod = mfaType;
      setMfaType(method as MfaType);
      currentMethodRef.current = method as MfaType; // Update ref immediately

      // Update alternative methods to show the previous method as alternative
      if (previousMethod && previousMethod !== method) {
        const updatedAlternativeMethods = alternativeMethods.filter(alt => alt.method !== method);

        // Add the previous method as an alternative
        const previousMethodDisplay = {
          'totp': 'Authenticator App',
          'email': 'Email OTP',
          'phone': 'SMS OTP'
        }[previousMethod] || previousMethod;

        updatedAlternativeMethods.unshift({
          method: previousMethod,
          display_name: previousMethodDisplay
        });

        setAlternativeMethods(updatedAlternativeMethods);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError('otp', {
        type: 'manual',
        message: error instanceof Error ? error.message : 'Failed to request OTP'
      });
    }
  }, [control, setError, mfaType, alternativeMethods]);

  return {
    // Form state
    control,
    formState,
    handleSubmit,
    setError,
    trigger,

    // UI state
    passwordVisibility,
    loading,
    otpRequired,
    showSocialOtp,
    socialIdentifier,
    accessToken,

    // MFA state
    mfaType,
    initialType,
    alternativeMethods,

    // Handlers
    onSubmit,
    togglePasswordVisibility,
    toggleMfaMode,
    handleSocialOtpSuccess,
    isSubmitDisabled,
    requestAlternativeOtp,
    panelLogin,
    signOut
  };
}